import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ApiError,
  api,
  getSession,
  setSession,
  setUnauthenticatedHandler,
} from "../lib/api";
import type { PermissionKey } from "../lib/permissions";

/* ------------------------------------------------------------------- Types */

export interface TenantBranding {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  surfaceColor: string;
  accentColor: string;
  productName: string;
  tagline: string | null;
  supportEmail: string | null;
  hideWolfrayBranding: boolean;
}

export interface TenantContext {
  id: string;
  name: string;
  slug: string;
  defaultCurrency: string;
  defaultTimeZone: string;
  defaultCulture: string;
  branding: TenantBranding;
  status: string;
}

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  jobTitle: string | null;
  department: string | null;
  avatarUrl: string | null;
  kind: "Internal" | "External";
  portalAudience: string | null;
  isExternal: boolean;
  mfaEnabled: boolean;
  culture: string;
  timeZone: string | null;
  personId: string | null;
  tenant: TenantContext;
  permissions: string[];
  roles: string[];
  enabledModules: string[];
  accessibleEventIds: string[];
}

interface AuthResult {
  accessToken: string;
  refreshToken: string;
  expiresAtUtc: string;
  user: CurrentUser;
}

/**
 * The answer to a sign-in. Usually the session itself; for an account protected by an
 * authenticator app, a challenge that a code completes instead.
 */
interface LoginResult {
  mfaRequired: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAtUtc: string | null;
  user: CurrentUser | null;
  mfaChallengeToken: string | null;
  mfaChallengeExpiresAtUtc: string | null;
}

/** What a sign-in attempt led to: a session, or a second step that needs a code. */
export type SignInOutcome =
  | { kind: "signedIn" }
  | { kind: "mfaRequired"; challengeToken: string; expiresAtUtc: string | null };

export interface SessionState {
  user: CurrentUser | null;
  status: "loading" | "signedIn" | "signedOut";
  error: string | null;
  signIn: (
    email: string,
    password: string,
    tenantSlug?: string,
    rememberMe?: boolean,
  ) => Promise<SignInOutcome>;
  /** Completes a sign-in that asked for a code from the authenticator app, or a recovery code. */
  completeMfaSignIn: (challengeToken: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** True when the signed-in user holds this permission at any scope. */
  can: (permission: PermissionKey | string) => boolean;
  /** True when they hold every one of these. */
  canAll: (...permissions: (PermissionKey | string)[]) => boolean;
  /** True when they hold at least one. */
  canAny: (...permissions: (PermissionKey | string)[]) => boolean;
  /** True when the tenant has the module switched on. */
  hasModule: (moduleKey: string) => boolean;
}

const SessionContext = createContext<SessionState | null>(null);

/* ---------------------------------------------------------------- Provider */

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<SessionState["status"]>("loading");
  const [error, setError] = useState<string | null>(null);

  // A 401 from anywhere drops the session, so a screen never sits showing
  // stale data behind an expired token.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setUser(null);
      setStatus("signedOut");
    });

    return () => setUnauthenticatedHandler(null);
  }, []);

  const loadUser = useCallback(async () => {
    if (!getSession()?.accessToken) {
      setUser(null);
      setStatus("signedOut");
      return;
    }

    try {
      const me = await api.get<CurrentUser>("/auth/me");
      setUser(me);
      setStatus("signedIn");
      applyCulture(me);
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthenticated) {
        setSession(null);
        setUser(null);
        setStatus("signedOut");
        return;
      }

      // A backend that is down is not the same as being signed out; say so
      // rather than silently bouncing the user to the sign-in screen.
      setError(err instanceof Error ? err.message : "Could not reach the server.");
      setStatus("signedOut");
    }
  }, []);

  // Restoring the session from the server on mount. This is the case the rule is
  // explicitly for — synchronising with an external system — and there is nothing
  // to derive during render, because the answer is not in the browser yet.
  /* oxlint-disable react/set-state-in-effect */
  useEffect(() => {
    void loadUser();
  }, [loadUser]);
  /* oxlint-enable react/set-state-in-effect */

  // Both ways into a session — a password alone, or a password and a code — end here.
  const openSession = useCallback((result: AuthResult) => {
    setSession({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAtUtc: result.expiresAtUtc,
      tenantSlug: result.user.tenant.slug,
    });

    setUser(result.user);
    setStatus("signedIn");
    applyCulture(result.user);
  }, []);

  const signIn = useCallback<SessionState["signIn"]>(
    async (email, password, tenantSlug, rememberMe = false) => {
      setError(null);

      const result = await api.post<LoginResult>("/auth/login", {
        email,
        password,
        tenantSlug: tenantSlug || null,
        rememberMe,
      });

      // An account with an authenticator app answers with a challenge: no session exists yet.
      if (result.mfaRequired && result.mfaChallengeToken) {
        return {
          kind: "mfaRequired",
          challengeToken: result.mfaChallengeToken,
          expiresAtUtc: result.mfaChallengeExpiresAtUtc,
        };
      }

      openSession({
        accessToken: result.accessToken ?? "",
        refreshToken: result.refreshToken ?? "",
        expiresAtUtc: result.expiresAtUtc ?? "",
        user: result.user!,
      });

      return { kind: "signedIn" };
    },
    [openSession],
  );

  const completeMfaSignIn = useCallback<SessionState["completeMfaSignIn"]>(
    async (challengeToken, code) => {
      setError(null);

      const result = await api.post<AuthResult>("/auth/mfa/verify", { challengeToken, code });
      openSession(result);
    },
    [openSession],
  );

  const signOut = useCallback(async () => {
    const refreshToken = getSession()?.refreshToken;

    try {
      await api.post("/auth/logout", { refreshToken: refreshToken ?? null });
    } catch {
      // Signing out locally must succeed even when the server call does not.
    }

    setSession(null);
    setUser(null);
    setStatus("signedOut");
  }, []);

  const value = useMemo<SessionState>(() => {
    const held = new Set(user?.permissions ?? []);
    const modules = new Set(user?.enabledModules ?? []);

    return {
      user,
      status,
      error,
      signIn,
      completeMfaSignIn,
      signOut,
      refreshUser: loadUser,
      can: (permission) => held.has(permission),
      canAll: (...permissions) => permissions.every((p) => held.has(p)),
      canAny: (...permissions) => permissions.some((p) => held.has(p)),
      hasModule: (moduleKey) => modules.size === 0 || modules.has(moduleKey),
    };
  }, [user, status, error, signIn, completeMfaSignIn, signOut, loadUser]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Applies the signed-in user's language and writing direction to the document.
 * Right-to-left is a document-level setting; the layout mirrors from there.
 */
function applyCulture(user: CurrentUser): void {
  const culture = user.culture || user.tenant.defaultCulture || "en-GB";
  const language = culture.split("-")[0].toLowerCase();
  const rtl = ["ar", "he", "fa", "ur"].includes(language);

  document.documentElement.lang = culture;
  document.documentElement.dir = rtl ? "rtl" : "ltr";
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error("useSession must be used inside a SessionProvider.");
  }

  return context;
}

/** The signed-in user, for screens that are already behind the auth gate. */
export function useCurrentUser(): CurrentUser {
  const { user } = useSession();

  if (!user) {
    throw new Error("useCurrentUser was called outside an authenticated route.");
  }

  return user;
}
